import { API_BASE, remove, send, upload, type UploadOpts } from './client';
import type { FillState, GatewayState, LocationType, SessionKind, ShipmentStatus } from './common';
import type { GatewayCondition, GatewayIssue } from './gateways';
import { query, type BoundedTotal, type OffsetPage } from './paging';

/** A location with its region (the backend's LocationView). */
export interface Location {
  id: number;
  code: string;
  name: string;
  type: LocationType;
  regencyCode: string;
  regencyName: string;
  provinceCode: string;
  provinceName: string;
  address: string | null;
  lat: number | null;
  lng: number | null;
  createdAt: string;
  /** the distribution network: the location this one is supplied from (a hub's SPBG), and how many sit under it */
  parentLocationId?: number | null;
  parentName?: string | null;
  childCount?: number;
  /** the village (desa / kelurahan) and its district (kecamatan): always set for a hub, optional otherwise */
  villageCode?: string | null;
  villageName?: string | null;
  districtCode?: string | null;
  districtName?: string | null;
}

/** GET /locations: each row with its live counters. */
export interface LocationListItem extends Location {
  /** ACTIVE cylinders recorded here */
  stock: number;
  /** how many of them are full / empty (stock = stockFull + stockEmpty; a rollup); absent from an older backend */
  stockFull?: number;
  stockEmpty?: number;
  gateways: number;
  gatewaysOnline: number;
  /** tags in front of this location's readers right now */
  tagsPresent: number;
  lastArrivalAt: string | null;
  /** the cover photo (its card image: `coverUrl`), null without one, absent from a backend before photos */
  coverPhotoId?: number | null;
  /** stock of this location and every location under it (an SPBG's hubs, a rollup); absent from an older backend */
  networkStock?: number;
  /** cylinders here past their retest */
  testOverdue?: number;
}

/** One photo of a location (the backend's PhotoView); both sizes are WebP, immutable per id. */
export interface PhotoView {
  id: number;
  width: number;
  height: number;
  bytes: number;
  isCover: boolean;
  /** 0-based order in the gallery */
  position: number;
  createdAt: string;
  urls: { card: string; full: string };
}
export type PhotoSize = 'card' | 'full';
/** The image types the backend accepts as an upload body. */
export const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type PhotoType = (typeof PHOTO_TYPES)[number];

export interface LocationGateway {
  gwId: string;
  label: string | null;
  state: GatewayState;
  stateAt: string | null;
  lastHeartbeatAt: string | null;
  version: string | null;
  tagsPresent: number;
  /** the server's condition of the reader, when this answer carries it (see conditionOf) */
  condition?: GatewayCondition;
  issues?: GatewayIssue[];
}

export interface StockCylinder {
  id: number;
  serialNo: string;
  epc: string;
  kind: string;
  capacity: string | null;
  owner: string | null;
  /** YYYY-MM-DD */
  nextTestDue: string | null;
  lastSeenAt: string | null;
  testOverdue: boolean;
  /** what it holds (absent from an older backend) */
  fillState?: FillState;
}

export type MovementDirection = 'inbound' | 'outbound';

export interface LocationMovement {
  id: number;
  at: string;
  direction: MovementDirection;
  cylinderId: number;
  serialNo: string;
  fromLocationId: number | null;
  fromName: string | null;
  toLocationId: number | null;
  toName: string | null;
  shipmentId: number | null;
  gwId: string | null;
}

/** Movements in and out on one WIB day. */
export interface DailyFlow {
  /** YYYY-MM-DD (WIB) */
  day: string;
  inbound: number;
  outbound: number;
}

/** GET /locations/:id: every part bounded, whatever the location's size. */
export interface LocationDetail extends Location {
  gateways: LocationGateway[];
  /** the total, how many are past their retest, and the newest registered among them (a rollup: a few seconds behind) */
  stock: { total: number; testOverdue: number; items: StockCylinder[]; /** full and empty among `total` (newer backends) */ full?: number; empty?: number };
  /** the newest few (a preview); the full list is GET /movements?locationId= */
  movements: LocationMovement[];
  /** how many movements in all (bounded); absent from an older backend */
  movementsTotal?: BoundedTotal | null;
  daily: DailyFlow[];
  /** in gallery order; absent from a backend that has no photos yet (0.4.0): the gallery then stays hidden */
  photos?: PhotoView[];
  coverPhotoId?: number | null;
  /** stock of this location and everything under it, when the backend sends it */
  networkStock?: number;
}

/** GET /locations/map: every location of the scope with its readers, for the map (unpaged). `stock` comes from a
 * rollup that follows a change within a few seconds. */
export interface MapLocation {
  id: number;
  code: string;
  name: string;
  type: LocationType;
  lat: number | null;
  lng: number | null;
  regencyCode: string;
  provinceCode: string;
  villageCode?: string | null;
  stock: number;
  /** how many of `stock` are full (newer backends) */
  stockFull?: number;
  gateways: { gwId: string; label: string | null; state: GatewayState }[];
  /** as on LocationListItem */
  coverPhotoId?: number | null;
}

/** GET /locations/:id/session: the load or unload currently running at a location (one at a time), or null. */
export interface LocationSession {
  id: number;
  kind: SessionKind;
  startedAt: string;
  shipmentId: number;
  number: string | null;
  status: ShipmentStatus;
  originLocationId: number;
  destinationLocationId: number;
  startedByName: string | null;
}

/** What the location form sends (POST, or PATCH with the same fields). */
export interface LocationInput {
  code: string;
  name: string;
  type: LocationType;
  regencyCode: string;
  address: string | null;
  lat: number | null;
  lng: number | null;
  /** null = no parent */
  parentLocationId: number | null;
  /** the village (required for a hub); null clears it */
  villageCode: string | null;
}

/**
 * GET /locations/destinations: where a Surat Jalan may go, nationwide whatever the sender's scope (a parent, a
 * neighbouring province, an off-network place, which is alerted, not refused). Light: no stock, readers or photos.
 */
export interface DestinationItem {
  id: number;
  code: string;
  name: string;
  type: LocationType;
  regencyCode: string;
  regencyName: string;
  provinceCode: string;
  provinceName: string;
  parentLocationId: number | null;
  villageCode?: string | null;
  villageName?: string | null;
}
export type DestinationPage = OffsetPage<DestinationItem>;

/**
 * The list filters (GET /locations, and all but paging for GET /locations/map). `type` and `status` are
 * comma-separated lists; `roots`: the top of the network tree (no parent, or a parent outside the caller's scope);
 * `parentId`: the direct children of one location; `network: 'mine'`: the operator's own location and everything
 * under it (for others: their scope).
 */
export interface LocationFilter { page?: number; limit?: number; type?: LocationType | string; provinceCode?: string; regencyCode?: string; districtCode?: string; villageCode?: string; q?: string; status?: string; parentId?: number; roots?: boolean; network?: 'mine' | '' }
/** `status=` values of the location lists and map. */
export const LOCATION_STATUSES = ['reader_offline', 'no_reader', 'stock_empty', 'off_network'] as const;
export type LocationStatus = (typeof LOCATION_STATUSES)[number];
/** What GET /locations/map filters on (unpaged: the whole scope). */
export type LocationMapFilter = Pick<LocationFilter, 'q' | 'provinceCode' | 'regencyCode' | 'districtCode' | 'villageCode' | 'type' | 'status'>;
export type LocationPage = OffsetPage<LocationListItem>;

type Id = number | string | undefined;

export const locationPaths = {
  list: (f: LocationFilter = {}) => `/locations${query({ ...f })}`,
  /** needs shipment.operate; offset pages by name */
  destinations: (f: Pick<LocationFilter, 'q' | 'type' | 'provinceCode' | 'parentId' | 'page' | 'limit'> = {}) => `/locations/destinations${query({ ...f })}`,
  /** the whole scope, unpaged; national without a province */
  map: (f: LocationMapFilter = {}) => `/locations/map${query({ ...f })}`,
  /** needs location.manage: a free code like HUB-RGN for this type and name */
  codeSuggestion: (type: LocationType, name: string) => `/locations/code-suggestion${query({ type, name })}`,
  detail: (id: Id) => `/locations/${id}`,
  session: (id: Id) => `/locations/${id}/session`,
  photos: (id: Id) => `/locations/${id}/photos`,
  photo: (id: Id, photoId: number) => `/locations/${id}/photos/${photoId}`,
};

/** Where an image of a location photo is served (cookie auth, cached for good: a photo never changes under its id). */
export const photoUrl = (locationId: number, photoId: number, size: PhotoSize) => `${API_BASE}${locationPaths.photo(locationId, photoId)}/${size}`;
/** The card image of a location's cover, or null when it has none (`undefined`: a backend without photos). */
export const coverUrl = (l: { id: number; coverPhotoId?: number | null }) => (l.coverPhotoId != null ? photoUrl(l.id, l.coverPhotoId, 'card') : null);

/** Admin: add a photo (the image itself as the body, with its type). */
export const uploadLocationPhoto = (id: number, image: Blob, opts?: UploadOpts) => upload<PhotoView>(locationPaths.photos(id), image, opts);
/** Admin: make a photo the cover, or move it to another place in the order; answers the whole gallery. */
export const updateLocationPhoto = (id: number, photoId: number, change: { isCover?: true; position?: number }) => send<PhotoView[]>(locationPaths.photo(id, photoId), change, 'PATCH');
export const deleteLocationPhoto = (id: number, photoId: number) => remove(locationPaths.photo(id, photoId));

/** Create (no id) or update a location. */
/** GET /locations/code-suggestion */
export interface CodeSuggestion { code: string }

export const saveLocation = (body: LocationInput, id?: number) => send<Location>(id ? `/locations/${id}` : '/locations', body, id ? 'PATCH' : 'POST');
export const deleteLocation = (id: number) => remove(`/locations/${id}`);
